import _extends from "@babel/runtime/helpers/extends";
import React, { forwardRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { space, width } from 'styled-system';
import { Spinner as BPSpinner } from '@blueprintjs/core';
import position from './utils/position';
const BaseSpinner = styled(BPSpinner).withConfig({
  displayName: "Spinner__BaseSpinner",
  componentId: "sc-1433tjb-0"
})(["&&{", ";", ";", ";}"], space, width, position);

// A single grain pulses bright once per cycle. Offsetting the delay down each
// column makes the bright grain appear to fall (left tube) / rise (right tube).
const grainflow = keyframes(["0%{opacity:0.14;}9%{opacity:1;}30%{opacity:0.14;}100%{opacity:0.14;}"]);
const HOURGLASS_DURATION = 0.9; // seconds, full cycle
const DOTS_PER_COLUMN = 4; // fewer, chunkier grains read better at small sizes
const DOT_SIZE = 2; // each grain is DOT_SIZE x DOT_SIZE units
const DOT_PITCH = 3; // distance between successive dot origins in a column
const COLUMN_GAP = 3; // gap between the two tubes
const VB_WIDTH = DOT_SIZE + COLUMN_GAP + DOT_SIZE; // viewBox width
const VB_HEIGHT = (DOTS_PER_COLUMN - 1) * DOT_PITCH + DOT_SIZE; // viewBox height
const RIGHT_COLUMN_X = DOT_SIZE + COLUMN_GAP; // x origin of right tube

const HourglassSvg = styled.svg.withConfig({
  displayName: "Spinner__HourglassSvg",
  componentId: "sc-1433tjb-1"
})(["&&{display:inline-block;color:", ";", ";", ";", ";rect{fill:currentColor;shape-rendering:crispEdges;animation:", " ", "s linear infinite;animation-delay:var(--d,0s);}@media (prefers-reduced-motion:reduce){rect{animation:none;opacity:0.55;}}}"], ({
  theme,
  color
}) => color && theme.colors[color] || 'currentColor', space, width, position, grainflow, HOURGLASS_DURATION);
const HourglassSpinner = /*#__PURE__*/forwardRef(({
  size = 16,
  intent,
  ...rest
}, ref) => {
  const step = HOURGLASS_DURATION / DOTS_PER_COLUMN;
  const dots = [];
  const numericSize = Number(size) || 16;
  for (let i = 0; i < DOTS_PER_COLUMN; i += 1) {
    const y = i * DOT_PITCH;
    const leftDelay = (i * step).toFixed(3);
    const rightDelay = ((DOTS_PER_COLUMN - 1 - i) * step).toFixed(3);

    // left tube: grain falls (delay grows top -> bottom)
    dots.push(/*#__PURE__*/React.createElement("rect", {
      key: `l${i}`,
      x: 0,
      y: y,
      width: DOT_SIZE,
      height: DOT_SIZE,
      style: {
        '--d': `${leftDelay}s`
      }
    }));
    // right tube: grain rises (delays reversed)
    dots.push(/*#__PURE__*/React.createElement("rect", {
      key: `r${i}`,
      x: RIGHT_COLUMN_X,
      y: y,
      width: DOT_SIZE,
      height: DOT_SIZE,
      style: {
        '--d': `${rightDelay}s`
      }
    }));
  }
  return /*#__PURE__*/React.createElement(HourglassSvg, _extends({
    ref: ref
    // Bind to the precise type mapped by styled-components/styled-system to fix the intersection error
    ,
    width: numericSize * VB_WIDTH / VB_HEIGHT,
    height: numericSize,
    viewBox: `0 0 ${VB_WIDTH} ${VB_HEIGHT}`,
    xmlns: "http://www.w3.org/2000/svg",
    role: "status",
    "aria-label": "Loading",
    intent: intent
  }, rest), dots);
});

// The generic ref expects EITHER a BlueprintJS Spinner instance or our SVG element
const Spinner = /*#__PURE__*/forwardRef(({
  variant,
  ...props
}, ref) => {
  if (variant === 'hourglass') {
    return /*#__PURE__*/React.createElement(HourglassSpinner, _extends({
      ref: ref
    }, props));
  }
  return /*#__PURE__*/React.createElement(BaseSpinner, _extends({
    ref: ref
  }, props));
});
export default Spinner;